import Phaser from 'phaser';
import { loginWithGoogle } from '../network/api.js';
import { GOOGLE_CLIENT_ID, waitForGoogleSdk } from '../network/google.js';
import { isApproved, type SessionUser } from '../network/session.js';
import { resetOverlay } from '../ui/overlay.js';
import { transitionTo } from '../ui/transition.js';

export class LoginScene extends Phaser.Scene {
  private overlay: HTMLDivElement | null = null;

  constructor() {
    super('Login');
  }

  create(): void {
    resetOverlay();
    this.add
      .text(this.scale.width / 2, 80, 'Dalmuti Online', {
        fontFamily: 'Georgia, serif',
        fontSize: '46px',
        color: '#8a5e1f',
        fontStyle: 'bold',
        stroke: '#fff7e0',
        strokeThickness: 2,
      })
      .setOrigin(0.5);
    // Original catchphrase of The Great Dalmuti.
    this.add
      .text(this.scale.width / 2, 124, "“Life isn't fair.”", {
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: '20px',
        color: '#c8922f',
        fontStyle: 'italic',
      })
      .setOrigin(0.5)
      .setAlpha(0.9);
    this.mountForm();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.unmountForm());
  }

  private goTo(key: string, data?: object): void {
    this.unmountForm();
    transitionTo(this, key, data);
  }

  // Route by approval status after a successful login. Approved users (admins
  // included) land in the lobby; admins reach the console via its header button.
  private routeByStatus(user: SessionUser): void {
    this.goTo(isApproved(user) ? 'Lobby' : 'Pending');
  }

  private mountForm(): void {
    const root = document.getElementById('overlay');
    if (!root) return;
    const wrap = document.createElement('div');
    wrap.className = 'ui-card';
    wrap.style.cssText =
      'left: 50%; top: 220px; transform: translateX(-50%); width: 340px; text-align:center;';
    wrap.innerHTML = `
      <h3 style="margin-top:0">구글 계정으로 로그인</h3>
      <p style="color:#9aa1ad; font-size:12px; margin:4px 0 16px;">
        최초 로그인 후 관리자 승인이 완료되면 게임에 입장할 수 있습니다.
      </p>
      <div id="dm-google-btn" style="min-height:40px; display:flex; justify-content:center;"></div>
      <div id="dm-error" style="color:#e07070; font-size:12px; min-height:16px; margin-top:12px;"></div>
    `;
    root.appendChild(wrap);
    this.overlay = wrap;

    const errorEl = wrap.querySelector<HTMLDivElement>('#dm-error')!;
    this.mountGoogleButton(wrap, errorEl);
  }

  private async mountGoogleButton(
    wrap: HTMLDivElement,
    errorEl: HTMLDivElement,
  ): Promise<void> {
    const slot = wrap.querySelector<HTMLDivElement>('#dm-google-btn');
    if (!slot) return;
    if (!GOOGLE_CLIENT_ID) {
      slot.innerHTML =
        '<span style="color:#9aa1ad; font-size:12px;">Google 로그인은 VITE_GOOGLE_CLIENT_ID 설정 후 사용 가능합니다.</span>';
      return;
    }
    try {
      const gsi = await waitForGoogleSdk();
      gsi.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response) => {
          errorEl.textContent = '';
          try {
            const user = await loginWithGoogle(response.credential);
            this.routeByStatus(user);
          } catch (err) {
            errorEl.textContent = (err as Error).message;
          }
        },
      });
      gsi.renderButton(slot, {
        type: 'standard',
        theme: 'filled_blue',
        size: 'large',
        text: 'continue_with',
        shape: 'pill',
      });
    } catch (err) {
      slot.innerHTML = `<span style="color:#9aa1ad; font-size:12px;">Google SDK 로드 실패: ${
        (err as Error).message
      }</span>`;
    }
  }

  private unmountForm(): void {
    this.overlay?.remove();
    this.overlay = null;
  }
}
